import React, { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/http";
import { useAuth } from "@/context/AuthContext";
import { fmtCurrency, CURRENCIES } from "@/lib/calc";
import { toast } from "sonner";
import { QuickSaleDialog } from "@/components/booking/QuickSaleDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import {
  ShoppingBag, ChevronRight, Coins, LockKeyhole, ArrowDownLeft, ArrowUpRight,
  Banknote, Wallet, ReceiptText,
} from "lucide-react";

const fmtTime = (iso) => { try { return new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }); } catch { return ""; } };

// ---- Petty cash dialog: open register, record cash in/out, see running drawer balance ----
const PettyCashDialog = ({ open, onOpenChange, cur, summary, reload }) => {
  const sym = CURRENCIES[cur]?.symbol || "$";
  const [dir, setDir] = useState("in");
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [float0, setFloat0] = useState("");
  const [busy, setBusy] = useState(false);
  const isOpen = !!summary?.session;

  useEffect(() => { if (open) { setDir("in"); setAmount(""); setReason(""); setFloat0(""); } }, [open]);

  const openRegister = async () => {
    setBusy(true);
    try {
      await api.post("/cash/open", { opening_float: parseFloat(float0) || 0 });
      toast.success("Register opened — drawer opens on the terminal");
      await reload();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Couldn't open register"); }
    finally { setBusy(false); }
  };

  const record = async () => {
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) { toast.error("Enter an amount"); return; }
    setBusy(true);
    try {
      await api.post("/cash/movement", { direction: dir, amount: amt, reason });
      toast.success(`${dir === "in" ? "Cash in" : "Cash out"} recorded — drawer opens on the terminal`);
      setAmount(""); setReason("");
      await reload();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Couldn't record"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[480px] max-h-[90vh] overflow-y-auto" data-testid="petty-cash-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2"><Coins className="h-4 w-4 text-primary" />Petty cash</DialogTitle>
          <DialogDescription>Record cash paid in or out. Selecting this opens the cash drawer on the terminal.</DialogDescription>
        </DialogHeader>

        {!isOpen ? (
          <div className="space-y-3">
            <div className="rounded-xl border border-dashed border-border p-4 text-sm text-muted-foreground">
              No register is open yet. Enter your starting cash float to begin the day.
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Opening float</label>
              <div className="relative"><span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">{sym}</span>
                <Input type="number" min="0" step="0.01" value={float0} onChange={(e) => setFloat0(e.target.value)} placeholder="0.00" className="h-11 rounded-lg pl-7 tabular-nums" data-testid="petty-open-float" /></div>
            </div>
            <Button onClick={openRegister} disabled={busy} className="rounded-lg w-full" data-testid="petty-open-register">{busy ? "Opening…" : "Open register"}</Button>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="rounded-xl bg-primary/5 border border-primary/20 p-4 flex items-center justify-between" data-testid="petty-drawer-balance">
              <div>
                <div className="text-xs uppercase tracking-wide text-muted-foreground">Cash in drawer</div>
                <div className="font-heading font-extrabold text-2xl tabular-nums text-primary">{fmtCurrency(summary.expected_balance, cur, 2)}</div>
              </div>
              <Banknote className="h-8 w-8 text-primary/40" />
            </div>

            <div className="grid grid-cols-2 gap-2">
              <button onClick={() => setDir("in")} className={`rounded-lg border p-2.5 text-sm font-medium flex items-center justify-center gap-1.5 transition-colors ${dir === "in" ? "border-emerald-500 bg-emerald-50 text-emerald-700" : "border-border text-muted-foreground"}`} data-testid="petty-dir-in"><ArrowDownLeft className="h-4 w-4" />Cash in</button>
              <button onClick={() => setDir("out")} className={`rounded-lg border p-2.5 text-sm font-medium flex items-center justify-center gap-1.5 transition-colors ${dir === "out" ? "border-rose-500 bg-rose-50 text-rose-700" : "border-border text-muted-foreground"}`} data-testid="petty-dir-out"><ArrowUpRight className="h-4 w-4" />Cash out</button>
            </div>

            <div>
              <label className="text-xs text-muted-foreground">Amount</label>
              <div className="relative"><span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">{sym}</span>
                <Input type="number" min="0" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0.00" className="h-11 rounded-lg pl-7 tabular-nums" data-testid="petty-amount" /></div>
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Reason</label>
              <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder={dir === "in" ? "e.g. Change top-up" : "e.g. Milk & supplies"} className="h-11 rounded-lg" data-testid="petty-reason" />
            </div>
            <Button onClick={record} disabled={busy} className="rounded-lg w-full" data-testid="petty-record">{busy ? "Saving…" : `Record ${dir === "in" ? "cash in" : "cash out"}`}</Button>

            {summary.movements?.length > 0 && (
              <div className="pt-1">
                <div className="text-xs font-medium text-muted-foreground mb-1.5">Today's movements</div>
                <div className="space-y-1 max-h-44 overflow-y-auto" data-testid="petty-movements">
                  {summary.movements.map((m) => (
                    <div key={m.id} className="flex items-center justify-between text-sm py-1 border-b border-border/50 last:border-0" data-testid={`petty-movement-${m.id}`}>
                      <div className="min-w-0">
                        <div className="truncate">{m.reason || (m.category === "float" ? "Opening float" : (m.direction === "in" ? "Cash in" : "Cash out"))}</div>
                        <div className="text-[11px] text-muted-foreground">{fmtTime(m.created_at)} · {m.staff_name || "Staff"}</div>
                      </div>
                      <span className={`tabular-nums font-medium shrink-0 ml-2 ${m.direction === "in" ? "text-emerald-600" : "text-rose-600"}`}>{m.direction === "in" ? "+" : "−"}{fmtCurrency(m.amount, cur, 2)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

// ---- Close register: count the drawer, reconcile expected vs counted (over/short) ----
const CloseRegisterDialog = ({ open, onOpenChange, cur, summary, reload }) => {
  const sym = CURRENCIES[cur]?.symbol || "$";
  const [counted, setCounted] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const isOpen = !!summary?.session;

  useEffect(() => { if (open) { setCounted(""); setNotes(""); setResult(null); } }, [open]);

  const expected = summary?.expected_balance || 0;
  const variance = counted === "" ? null : Math.round(((parseFloat(counted) || 0) - expected) * 100) / 100;

  const close = async () => {
    if (counted === "") { toast.error("Enter the counted amount"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/cash/close", { counted_amount: parseFloat(counted) || 0, notes });
      setResult(data);
      await reload();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Couldn't close register"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[460px] max-h-[90vh] overflow-y-auto" data-testid="close-register-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2"><LockKeyhole className="h-4 w-4 text-primary" />Close register</DialogTitle>
          <DialogDescription>Count the drawer at end of day and reconcile against what's expected.</DialogDescription>
        </DialogHeader>

        {result ? (
          <div className="space-y-3" data-testid="close-result">
            <div className={`rounded-xl p-4 text-center ${result.variance === 0 ? "bg-emerald-50 border border-emerald-200" : Math.abs(result.variance) > 0 ? "bg-amber-50 border border-amber-200" : ""}`}>
              <div className="text-xs uppercase tracking-wide text-muted-foreground">{result.variance === 0 ? "Balanced" : result.variance > 0 ? "Over" : "Short"}</div>
              <div className={`font-heading font-extrabold text-2xl tabular-nums ${result.variance === 0 ? "text-emerald-700" : "text-amber-700"}`}>{result.variance > 0 ? "+" : ""}{fmtCurrency(result.variance, cur, 2)}</div>
            </div>
            <div className="rounded-xl border border-border p-3 space-y-1 text-sm">
              <Row label="Expected in drawer" val={fmtCurrency(result.expected_amount, cur, 2)} cur={cur} />
              <Row label="Counted" val={fmtCurrency(result.counted_amount, cur, 2)} cur={cur} />
            </div>
            <Button onClick={() => onOpenChange(false)} className="rounded-lg w-full" data-testid="close-done">Done</Button>
          </div>
        ) : !isOpen ? (
          <div className="rounded-xl border border-dashed border-border p-4 text-sm text-muted-foreground" data-testid="close-no-register">No register is open. Open one from Petty cash first.</div>
        ) : (
          <div className="space-y-4">
            <div className="rounded-xl border border-border p-3 space-y-1 text-sm" data-testid="close-expected-breakdown">
              <Row label="Opening float" val={fmtCurrency(summary.opening_float, cur, 2)} />
              <Row label="Cash sales" val={fmtCurrency(summary.cash_sales, cur, 2)} />
              <Row label="Cash in" val={`+${fmtCurrency(summary.pay_in, cur, 2)}`} className="text-emerald-600" />
              <Row label="Cash out" val={`−${fmtCurrency(summary.pay_out, cur, 2)}`} className="text-rose-600" />
              <div className="flex justify-between font-heading font-bold pt-1 border-t border-border/60"><span>Expected in drawer</span><span className="tabular-nums" data-testid="close-expected">{fmtCurrency(expected, cur, 2)}</span></div>
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Counted amount (what's physically in the drawer)</label>
              <div className="relative"><span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">{sym}</span>
                <Input type="number" min="0" step="0.01" value={counted} onChange={(e) => setCounted(e.target.value)} placeholder="0.00" className="h-11 rounded-lg pl-7 tabular-nums" data-testid="close-counted" /></div>
            </div>
            {variance !== null && (
              <div className={`rounded-lg p-2.5 text-sm text-center font-medium ${variance === 0 ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`} data-testid="close-variance">
                {variance === 0 ? "Balanced" : `${variance > 0 ? "Over" : "Short"} ${fmtCurrency(Math.abs(variance), cur, 2)}`}
              </div>
            )}
            <div>
              <label className="text-xs text-muted-foreground">Notes (optional)</label>
              <Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Anything to note about today's till" className="h-11 rounded-lg" data-testid="close-notes" />
            </div>
            <Button onClick={close} disabled={busy} className="rounded-lg w-full" data-testid="close-confirm">{busy ? "Closing…" : "Close register"}</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

const Row = ({ label, val, className = "" }) => (
  <div className="flex justify-between"><span className="text-muted-foreground">{label}</span><span className={`tabular-nums ${className}`}>{val}</span></div>
);

const MenuRow = ({ icon: Icon, label, sub, onClick, testid, danger }) => (
  <button onClick={onClick} data-testid={testid} className="w-full flex items-center gap-3 px-4 py-3.5 hover:bg-muted/50 transition-colors text-left">
    <span className={`h-9 w-9 rounded-lg flex items-center justify-center shrink-0 ${danger ? "bg-rose-100 text-rose-600" : "bg-primary/10 text-primary"}`}><Icon className="h-4.5 w-4.5" /></span>
    <span className="flex-1 min-w-0">
      <span className="block font-medium">{label}</span>
      {sub && <span className="block text-xs text-muted-foreground truncate">{sub}</span>}
    </span>
    <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
  </button>
);

export default function Sales() {
  const { business } = useAuth();
  const cur = business?.currency || "AUD";
  const [summary, setSummary] = useState(null);
  const [saleOpen, setSaleOpen] = useState(false);
  const [pettyOpen, setPettyOpen] = useState(false);
  const [closeOpen, setCloseOpen] = useState(false);

  const reload = useCallback(async () => {
    try { const { data } = await api.get("/cash/session"); setSummary(data); }
    catch { setSummary(null); }
  }, []);
  useEffect(() => { reload(); }, [reload]);

  const isOpen = !!summary?.session;

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8" data-testid="sales-page">
      <div className="mb-6">
        <h1 className="font-heading font-extrabold text-2xl sm:text-3xl tracking-tight">Sales</h1>
        <p className="text-muted-foreground mt-1">Take payments and manage the cash drawer</p>
      </div>

      <div className="grid lg:grid-cols-5 gap-6">
        {/* Drawer status */}
        <div className="lg:col-span-3 space-y-4">
          <div className="rounded-2xl border bg-card p-6 shadow-sm" data-testid="sales-drawer-card">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-sm text-muted-foreground"><Wallet className="h-4 w-4" />Cash drawer</div>
              <span className={`text-xs px-2 py-0.5 rounded-full ${isOpen ? "bg-emerald-100 text-emerald-700" : "bg-muted text-muted-foreground"}`} data-testid="sales-register-status">{isOpen ? "Register open" : "Register closed"}</span>
            </div>
            <div className="mt-3">
              <div className="font-heading font-extrabold text-4xl tabular-nums" data-testid="sales-drawer-amount">{fmtCurrency(summary?.expected_balance || 0, cur, 2)}</div>
              <div className="text-sm text-muted-foreground mt-1">Expected cash in drawer</div>
            </div>
            {isOpen && (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-5">
                <Stat label="Opening float" val={fmtCurrency(summary.opening_float, cur, 0)} />
                <Stat label="Cash sales" val={fmtCurrency(summary.cash_sales, cur, 0)} />
                <Stat label="Cash in" val={`+${fmtCurrency(summary.pay_in, cur, 0)}`} />
                <Stat label="Cash out" val={`−${fmtCurrency(summary.pay_out, cur, 0)}`} />
              </div>
            )}
          </div>
        </div>

        {/* Sales menu (Timely-style) */}
        <div className="lg:col-span-2">
          <div className="rounded-2xl border bg-card shadow-sm overflow-hidden" data-testid="sales-menu">
            <div className="px-4 pt-4 pb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Sell</div>
            <MenuRow icon={ShoppingBag} label="New sale" sub="Service, retail, bundle, voucher or credit" onClick={() => setSaleOpen(true)} testid="sales-new-sale" />
            <div className="border-t border-border" />
            <div className="px-4 pt-4 pb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Cash management</div>
            <MenuRow icon={Coins} label="Petty cash" sub="Cash in / out · opens the drawer" onClick={() => setPettyOpen(true)} testid="sales-petty-cash" />
            <div className="border-t border-border" />
            <MenuRow icon={LockKeyhole} label="Close register" sub={isOpen ? "Count & reconcile the drawer" : "No register open"} onClick={() => setCloseOpen(true)} testid="sales-close-register" danger />
          </div>
        </div>
      </div>

      <QuickSaleDialog open={saleOpen} onOpenChange={setSaleOpen} cur={cur} onDone={reload} />
      <PettyCashDialog open={pettyOpen} onOpenChange={setPettyOpen} cur={cur} summary={summary} reload={reload} />
      <CloseRegisterDialog open={closeOpen} onOpenChange={setCloseOpen} cur={cur} summary={summary} reload={reload} />
    </div>
  );
}

const Stat = ({ label, val }) => (
  <div className="rounded-xl bg-muted/40 p-3">
    <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</div>
    <div className="font-heading font-bold tabular-nums mt-0.5">{val}</div>
  </div>
);
